import React, { useEffect, useState, createContext, useContext, PropsWithChildren, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { bindFormChange } from './Util';
import { APIResponse, StrictAPIResponse } from './Classes';
import Validator, { ValidatorFormRules } from './Validator';
import usePagination from './src/hooks/usePagination';

const useScript = (url: string) => {
	useEffect(() => {
		const script = document.createElement('script');
		
		script.src = url;
		script.async = true;
		
		document.body.appendChild(script);
		
		return () => {
			document.body.removeChild(script);
		}
	}, [url]);
};

var TitleContext = createContext<{ setTitle: (title: string)=>void }>({ setTitle: (v: string)=>null });
var useTitle = () => useContext(TitleContext);

interface TitleProps extends PropsWithChildren{
	default?: string,
	suffix?: string,
}

var TitleProvider = (props: TitleProps) => {
	var [titleHistory, setTitleHistory] = useState<{ pathname: string, title: string }[]>([]);
	var location = useLocation();

	useEffect(()=>{
		var suffix = props.suffix || 'AREMA Ticket';
		var title = titleHistory.find(a=>a.pathname===location.pathname);
		if(!title || !title.title){
			document.title = suffix;
		}else{
			document.title = `${title.title} - ${suffix}`;
		}
	}, [location, titleHistory]);

	var changeTitle = (title: string)=>{
		if(!location.key) return;
		var hs = [...titleHistory];
		hs = hs.filter(a=>a.pathname!==location.pathname);
		hs.push({ pathname: location.pathname, title });
		setTitleHistory(hs);
	}

	return (
		<TitleContext.Provider value={{ setTitle: changeTitle }}>
			{props.children}
		</TitleContext.Provider>
	)
}

const useValidator = <T,>(initValues: T, rules?: ValidatorFormRules<T>)=>{
	var [data, setData] = useState<T>(initValues);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [errors, setErrors] = useState<{
		[x in keyof T]: boolean
	}>({} as any);

	useEffect(()=>{
		if(data && typeof data==='object' && Object.keys(errors).length===0){
			var err : any = {};
			for(var i in data){
				err[i] = false;
			}
			setErrors(err);
			reset()
		}
	}, []);

	var reset = (init?: { [x in keyof T]?: any })=>{
		var dt : any = {};
		if(initValues && typeof initValues==='object'){
			for(var v in initValues){
				dt[v] = initValues[v];
			}
		}
		if(init){
			for(var v in init){
				dt[v] = init[v];
			}
		}
		setData(dt);
	}

	var validate = (data_override?: any, rules_override?: ValidatorFormRules<T>) : { valid: boolean, prompts: string[], errors: typeof errors }=>{
		if(!rules) return { valid: true, prompts: [], errors };
		// console.log(data);
		// console.log({ ...data, ...data_override }, { ...rules, ...rules_override });
		var vld = Validator({ ...data, ...data_override }, { ...rules, ...rules_override });

		var nw_errors : any = {};
		for(var i in data){
			nw_errors[i] = vld.errors[i];
		}
		setErrors(nw_errors);
		setPrompts(vld.prompts);
		return {
			valid: vld.valid,
			prompts,
			errors: nw_errors
		}
	}

	var onDataChange = (name: keyof T, target: boolean=false)=>{
		if(errors[name] && rules && rules[name]){
			var valid = Validator.validate(rules[name], data[name], data);
			if(valid){
				setErrors(e=>({
					...e,
					[name]: !valid
				}));
			}
		}

		return bindFormChange(data, setData)(name, target);
	}

	var setValue = (name: keyof T, val: any)=>{
		return setData(d=>({
			...d,
			[name]: val
		}));
	}

	var updateErrors = (errs: typeof errors)=>{
		setErrors(errs);
	}

	var clearErrors = (initErrors?: typeof errors)=>{
		setErrors(initErrors || {} as any);
	}

	return {
		data,
		errors,
		onDataChange,
		setData,
		setValue,
		prompts,
		reset,
		setPrompts,
		validate,
		setErrors: updateErrors,
		clearErrors,
	}
}

interface APIHookOptions<K, T extends (...args: any[])=>Promise<APIResponse<K>>=any>{
	unexpected_error?: string,
	setLoadError?: (v: string)=>void,
	setLoadErrorArray?: (v: string[])=>void,
	onData?: (res: Awaited<ReturnType<T>>)=>void,
	clearOnLoad?: boolean,
}
function useAPI<K,T extends (...args: any[])=>Promise<StrictAPIResponse<K>>=any>(apiCall: T, opts?: APIHookOptions<K,T>){
	var [res, setRes] = useState<StrictAPIResponse<K>>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [loadError, setLoadError] = useState<string>(null);
	
	var loadCall = async (...params: Parameters<T>) : Promise<Awaited<ReturnType<T>>>=>{
		setLoading(true);
		setLoadError(null);
		if(opts?.setLoadError) opts.setLoadError(null);
		if(opts?.setLoadErrorArray) opts.setLoadErrorArray(null);
		if(opts?.clearOnLoad!==false) setRes(null);
		
		try{
			var res = await apiCall(...params);
			if(opts?.onData) opts.onData(res as any);
			setRes(res);
			if(res.error && (opts?.setLoadError || opts?.setLoadErrorArray)){
				if(opts?.setLoadError) opts.setLoadError(res.message);
				if(opts?.setLoadErrorArray) opts.setLoadErrorArray([res.message]);
			}
			return res as any;
		}catch(e){
			if(opts?.unexpected_error){
				setLoadError(opts.unexpected_error);
				if(opts?.setLoadError) opts.setLoadError(opts.unexpected_error);
				if(opts?.setLoadErrorArray) opts.setLoadErrorArray([opts.unexpected_error]);
			}else{
				throw e;
			}
		}finally{
			setLoading(false);
		}
	}

	var setData = (data: Awaited<ReturnType<T>>["data"])=>{
		return setRes({
			code: null,
			error: false,
			message: null,
			...res,
			data,
		})
	}

	return {
		load: loadCall,
		loading,
		error: (!!res && !!res.error) || false,
		loadError: loadError || (!!res && !!res.error ? res.message : null) as string,
		data: (!!res && res.data) as Awaited<ReturnType<T>>["data"] || null,
		setData
	}
}

export {
	TitleProvider,
	useAPI,
	usePagination,
	useScript,
	useTitle,
	useValidator,
};